<template>
  <div class="flex flex-col gap-y-4">
    <div>
      <h3>
        <Translation
          name="calculation_wizard.preview.sidebar_benefits"
          default="In-kind Benefits"
        />
      </h3>
      <div class="rounded-xl bg-[#3B3B44] p-4">
        <div class="flex flex-col gap-y-2 mb-6">
          <span class="text-white font-bold">
            <Translation
              name="calculation_wizard.preview.sidebar_you_entered"
              default="You entered"
            />
          </span>
          <div class="info-container">
            <span class="info-text">
              <Translation
                name="calculation_wizard.preview.sidebar_benefits_housing"
                default="Housing"
              />:
            </span>
            <div class="value-tag">
              {{ ikbHousing ? ikbHousing.toFixed(2) : "-" }} {{ currency }}
            </div>
          </div>
          <div class="info-container">
            <span class="info-text">
              <Translation
                name="calculation_wizard.preview.sidebar_benefits_childcare"
                default="Childcare"
              />:
            </span>
            <div class="value-tag">
              {{ ikbChildcare ? ikbChildcare.toFixed(2) : "-" }} {{ currency }}
            </div>
          </div>
          <div class="info-container">
            <span class="info-text">
              <Translation
                name="calculation_wizard.preview.sidebar_benefits_child_education"
                default="Child education"
              />:
            </span>
            <div class="value-tag">
              {{ ikbChildEducation ? ikbChildEducation.toFixed(2) : "-" }}
              {{ currency }}
            </div>
          </div>
          <div class="info-container">
            <span class="info-text">
              <Translation
                name="calculation_wizard.preview.sidebar_benefits_food"
                default="Food"
              />:
            </span>
            <div class="value-tag">
              {{ ikbFood ? ikbFood.toFixed(2) : "-" }} {{ currency }}
            </div>
          </div>
          <div class="info-container">
            <span class="info-text">
              <Translation
                name="calculation_wizard.preview.sidebar_benefits_healthcare"
                default="Healthcare"
              />:
            </span>
            <div class="value-tag">
              {{ ikbHealthcare ? ikbHealthcare.toFixed(2) : "-" }}
              {{ currency }}
            </div>
          </div>
          <div class="info-container">
            <span class="info-text">
              <Translation
                name="calculation_wizard.preview.sidebar_benefits_transportation"
                default="Transportation"
              />:
            </span>
            <div class="value-tag">
              {{ ikbTransportation ? ikbTransportation.toFixed(2) : "-" }}
              {{ currency }}
            </div>
          </div>
        </div>
        <div class="flex flex-col gap-y-2">
          <span class="text-white font-bold">
            <Translation
              name="calculation_wizard.preview.sidebar_chart_shows"
              default="The chart shows"
            />
          </span>
          <div class="info-container">
            <span class="info-text">
              <Translation
                name="calculation_wizard.preview.sidebar_benefits_monthly_ikb"
                default="Comparable monthly in-kind benefits: "
              />
            </span>
            <div class="value-tag">
              {{
                comparableMonthlyInKindBenefits
                  ? comparableMonthlyInKindBenefits.toFixed(2)
                  : "-"
              }}
              {{ currency }}
            </div>
          </div>
        </div>
      </div>
    </div>
    <div v-if="hasBenefits">
      <h3>
        <Translation
          name="calculation_wizard.preview.sidebar_calculation"
          default="Calculation"
        />
      </h3>
      <div v-if="capsApplied" class="rounded-xl bg-[#3B3B44] p-4 mb-4">
        <div class="flex flex-col gap-y-2">
          <p class="info-text">
            <Translation
              name="calculation_wizard.preview.sidebar_benefits_calculation_p1"
              default="Each in-kind benefit has a maximum value relative to the total remuneration, depending on the type and the number of in-kind benefits provided. There is also a maximum to the sum of all in-kind benefits. For this job category or individual, one or more of these caps are exceeded."
            />
          </p>
          <p class="info-text">
            <Translation
              name="calculation_wizard.preview.sidebar_benefits_calculation_p2"
              default="These caps are defined relative to the 'cash wages', which are the sum of 'comparable annual full-time wages' and 'comparable annual bonuses'. The caps are:"
            />
          </p>
          <div class="bg-[#2D2D32] rounded p-3 mt-3 border border-gray-600">
            <p class="text-sm text-gray-200 font-bold mb-2">
              <Translation
                name="calculation_wizard.preview.sidebar_benefits_calculation_one_ikb"
                default="With one in-kind benefit"
              />
            </p>
            <p class="text-sm text-gray-200">
              <Translation
                name="calculation_wizard.preview.sidebar_benefits_calculation_housing"
                default="Housing benefits:"
              />
              17.6%
              <Translation
                name="calculation_wizard.preview.sidebar_benefits_of_cash_wages"
                default="of cash wages"
              />
            </p>
            <p class="text-sm text-gray-200">
              <Translation
                name="calculation_wizard.preview.sidebar_benefits_calculation_other"
                default="Other in-kind benefits:"
              />
              11.1%
              <Translation
                name="calculation_wizard.preview.sidebar_benefits_of_cash_wages"
                default="of cash wages"
              />
            </p>
            <p class="text-sm text-gray-200">
              <Translation
                name="calculation_wizard.preview.sidebar_benefits_calculation_total"
                default="Total in-kind benefits:"
              />
              42.9%
              <Translation
                name="calculation_wizard.preview.sidebar_benefits_of_cash_wages"
                default="of cash wages"
              />
            </p>
          </div>
          <div class="bg-[#2D2D32] rounded p-3 mt-3 border border-gray-600">
            <p class="text-sm text-gray-200 font-bold mb-2">
              <Translation
                name="calculation_wizard.preview.sidebar_benefits_calculation_more_ikb"
                default="With more than one in-kind benefit"
              />
            </p>
            <p class="text-sm text-gray-200">
              <Translation
                name="calculation_wizard.preview.sidebar_benefits_calculation_housing"
                default="Housing benefits:"
              />
              19.5%
              <Translation
                name="calculation_wizard.preview.sidebar_benefits_of_cash_wages"
                default="of cash wages"
              />
            </p>
            <p class="text-sm text-gray-200">
              <Translation
                name="calculation_wizard.preview.sidebar_benefits_calculation_other"
                default="Other in-kind benefits:"
              />
              12.7%
              <Translation
                name="calculation_wizard.preview.sidebar_benefits_of_cash_wages"
                default="of cash wages"
              />
            </p>
            <p class="text-sm text-gray-200">
              <Translation
                name="calculation_wizard.preview.sidebar_benefits_calculation_total"
                default="Total in-kind benefits:"
              />
              42.9%
              <Translation
                name="calculation_wizard.preview.sidebar_benefits_of_cash_wages"
                default="of cash wages"
              />
            </p>
          </div>
        </div>
      </div>
      <div class="flex flex-col gap-y-2">
        <template v-if="capsApplied">
          <div id="step-1" class="accordion" v-if="capsExceeded">
            <div
              class="flex items-center justify-between cursor-pointer"
              @click="toggleStep(1)"
            >
              <span class="font-bold">
                <Translation
                  name="calculation_wizard.preview.sidebar_step_1"
                  default="Step 1"
                />
              </span>
              <ChevronDownWhite
                class="transition-transform"
                :class="{ 'rotate-180': stepsOpen[1] }"
              />
            </div>
            <div
              class="info-text accordion-content max-h-0"
              :class="stepsOpen[1] ? 'opacity-1 mt-4' : 'opacity-0'"
            >
              <p>
                <Translation
                  name="calculation_wizard.preview.sidebar_benefits_step_1_p1"
                  default="One or more of the in-kind benefits for your job category / individual is above the maximum relative value for an individual benefit. Therefore, their value is reduced. The table below shows how:"
                />
              </p>
              <div
                class="bg-[#2D2D32] rounded p-3 mt-3 border border-gray-600 overflow-x-auto max-h-96"
              >
                <table class="w-full text-xs text-gray-200">
                  <thead>
                    <tr class="border-b border-gray-500">
                      <th class="text-left py-2 px-2">
                        <Translation
                          name="calculation_wizard.preview.sidebar_benefits_step_1_benefits_type"
                          default="Benefit Type"
                        />
                      </th>
                      <th class="text-right py-2 px-2">
                        <Translation
                          name="calculation_wizard.preview.sidebar_benefits_step_1_original"
                          default="Original"
                        />
                      </th>
                      <th class="text-right py-2 px-2">
                        <Translation
                          name="calculation_wizard.preview.sidebar_benefits_step_1_cap_percent"
                          default="Cap %"
                        />
                      </th>
                      <th class="text-right py-2 px-2">
                        <Translation
                          name="calculation_wizard.preview.sidebar_benefits_step_1_cap_value"
                          default="Cap Value"
                        />
                      </th>
                      <th class="text-right py-2 px-2">
                        <Translation
                          name="calculation_wizard.preview.sidebar_benefits_step_1_adjusted"
                          default="Adjusted"
                        />
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr class="border-b border-gray-600">
                      <td class="py-2 px-2">
                        <Translation
                          name="calculation_wizard.preview.sidebar_benefits_housing"
                          default="Housing"
                        />
                      </td>
                      <td class="text-right py-2 px-2">{{ ikbHousing }}</td>
                      <td class="text-right py-2 px-2">
                        {{ housingIkbCapPercentage }}%
                      </td>
                      <td class="text-right py-2 px-2">
                        {{
                          Math.round(
                            comparableWageAndBonuses *
                              (housingIkbCapPercentage / 100)
                          )
                        }}
                      </td>
                      <td class="text-right py-2 px-2">
                        {{
                          Math.min(
                            ikbHousing,
                            Math.round(
                              comparableWageAndBonuses *
                                (housingIkbCapPercentage / 100)
                            )
                          )
                        }}
                      </td>
                    </tr>
                    <tr class="border-b border-gray-600">
                      <td class="py-2 px-2">
                        <Translation
                          name="calculation_wizard.preview.sidebar_benefits_childcare"
                          default="Childcare"
                        />
                      </td>
                      <td class="text-right py-2 px-2">{{ ikbChildcare }}</td>
                      <td class="text-right py-2 px-2">
                        {{ otherIkbCapPercentage }}%
                      </td>
                      <td class="text-right py-2 px-2">
                        {{
                          Math.round(
                            comparableWageAndBonuses *
                              (otherIkbCapPercentage / 100)
                          )
                        }}
                      </td>
                      <td class="text-right py-2 px-2">
                        {{
                          Math.min(
                            ikbChildcare,
                            Math.round(
                              comparableWageAndBonuses *
                                (otherIkbCapPercentage / 100)
                            )
                          )
                        }}
                      </td>
                    </tr>
                    <tr class="border-b border-gray-600">
                      <td class="py-2 px-2">
                        <Translation
                          name="calculation_wizard.preview.sidebar_benefits_child_education"
                          default="Child education"
                        />
                      </td>
                      <td class="text-right py-2 px-2">
                        {{ ikbChildEducation }}
                      </td>
                      <td class="text-right py-2 px-2">
                        {{ otherIkbCapPercentage }}%
                      </td>
                      <td class="text-right py-2 px-2">
                        {{
                          Math.round(
                            comparableWageAndBonuses *
                              (otherIkbCapPercentage / 100)
                          )
                        }}
                      </td>
                      <td class="text-right py-2 px-2">
                        {{
                          Math.min(
                            ikbChildEducation,
                            Math.round(
                              comparableWageAndBonuses *
                                (otherIkbCapPercentage / 100)
                            )
                          )
                        }}
                      </td>
                    </tr>
                    <tr class="border-b border-gray-600">
                      <td class="py-2 px-2">
                        <Translation
                          name="calculation_wizard.preview.sidebar_benefits_food"
                          default="Food"
                        />
                      </td>
                      <td class="text-right py-2 px-2">{{ ikbFood }}</td>
                      <td class="text-right py-2 px-2">
                        {{ otherIkbCapPercentage }}%
                      </td>
                      <td class="text-right py-2 px-2">
                        {{
                          Math.round(
                            comparableWageAndBonuses *
                              (otherIkbCapPercentage / 100)
                          )
                        }}
                      </td>
                      <td class="text-right py-2 px-2">
                        {{
                          Math.min(
                            ikbFood,
                            Math.round(
                              comparableWageAndBonuses *
                                (otherIkbCapPercentage / 100)
                            )
                          )
                        }}
                      </td>
                    </tr>
                    <tr class="border-b border-gray-600">
                      <td class="py-2 px-2">
                        <Translation
                          name="calculation_wizard.preview.sidebar_benefits_healthcare"
                          default="Healthcare"
                        />
                      </td>
                      <td class="text-right py-2 px-2">{{ ikbHealthcare }}</td>
                      <td class="text-right py-2 px-2">
                        {{ otherIkbCapPercentage }}%
                      </td>
                      <td class="text-right py-2 px-2">
                        {{
                          Math.round(
                            comparableWageAndBonuses *
                              (otherIkbCapPercentage / 100)
                          )
                        }}
                      </td>
                      <td class="text-right py-2 px-2">
                        {{
                          Math.min(
                            ikbHealthcare,
                            Math.round(
                              comparableWageAndBonuses *
                                (otherIkbCapPercentage / 100)
                            )
                          )
                        }}
                      </td>
                    </tr>
                    <tr class="border-b border-gray-600">
                      <td class="py-2 px-2">
                        <Translation
                          name="calculation_wizard.preview.sidebar_benefits_transportation"
                          default="Transportation"
                        />
                      </td>
                      <td class="text-right py-2 px-2">
                        {{ ikbTransportation }}
                      </td>
                      <td class="text-right py-2 px-2">
                        {{ otherIkbCapPercentage }}%
                      </td>
                      <td class="text-right py-2 px-2">
                        {{
                          Math.round(
                            comparableWageAndBonuses *
                              (otherIkbCapPercentage / 100)
                          )
                        }}
                      </td>
                      <td class="text-right py-2 px-2">
                        {{
                          Math.min(
                            ikbTransportation,
                            Math.round(
                              comparableWageAndBonuses *
                                (otherIkbCapPercentage / 100)
                            )
                          )
                        }}
                      </td>
                    </tr>
                    <tr class="border-t-2 border-gray-400 font-bold">
                      <td class="py-2 px-2">
                        <Translation
                          name="calculation_wizard.preview.sidebar_benefits_total"
                          default="Total"
                        />
                      </td>
                      <td class="text-right py-2 px-2"></td>
                      <td class="text-right py-2 px-2"></td>
                      <td class="text-right py-2 px-2"></td>
                      <td class="text-right py-2 px-2">
                        {{ totalAdjustedBenefits }}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
          <div id="step-2" class="accordion" v-if="combinedCapsExceeded">
            <div
              class="flex items-center justify-between cursor-pointer"
              @click="toggleStep(2)"
            >
              <span class="font-bold">
                <Translation
                  v-if="stepTwoLabel == 'Step 1'"
                  name="calculation_wizard.preview.sidebar_step_1"
                  default="Step 1"
                />
                <Translation
                  v-else
                  name="calculation_wizard.preview.sidebar_step_2"
                  default="Step 2"
                />
              </span>
              <ChevronDownWhite
                class="transition-transform"
                :class="{ 'rotate-180': stepsOpen[2] }"
              />
            </div>
            <div
              class="info-text accordion-content max-h-0"
              :class="stepsOpen[2] ? 'opacity-1 mt-4' : 'opacity-0'"
            >
              <p>
                <Translation
                  name="calculation_wizard.preview.sidebar_benefits_step_2_p1"
                  default="The total value of the in-kind benefits for this job category or individual is above the maximum percentage of cash wages indicated above. Therefore, to calculate the total adjusted annual in-kind benefits, the tool uses the capped value:"
                />
              </p>
              <div class="bg-[#2D2D32] rounded p-3 mt-3 border border-gray-600">
                <p class="text-sm text-gray-200">
                  <Translation
                    name="calculation_wizard.preview.sidebar_benefits_step_2_sum"
                    default="Sum of individually adjusted in-kind benefits:"
                  />
                  <strong>{{ totalAdjustedBenefits.toFixed(2) }}</strong>
                  {{ currency }}
                </p>
                <p class="text-sm text-gray-200">
                  <Translation
                    name="calculation_wizard.preview.sidebar_benefits_step_2_cap"
                    default="Cap of annual in-kind benefits:"
                  />
                  <strong>{{ combinedCapValue.toFixed(2) }}</strong>
                  {{ currency }}
                </p>
                <hr class="my-2 border-gray-600" />
                <p class="text-sm text-gray-200">
                  <Translation
                    name="calculation_wizard.preview.sidebar_benefits_step_2_total"
                    default="Total adjusted annual in-kind benefits:"
                  />
                  <strong>{{ combinedCapValue.toFixed(2) }}</strong>
                  {{ currency }}
                </p>
              </div>
            </div>
          </div>
          <div id="step-3" class="accordion">
            <div
              class="flex items-center justify-between cursor-pointer"
              @click="toggleStep(3)"
            >
              <span class="font-bold">
                <Translation
                  v-if="stepThreeLabel == 'Step 1'"
                  name="calculation_wizard.preview.sidebar_step_1"
                  default="Step 1"
                />
                <Translation
                  v-else-if="stepThreeLabel == 'Step 2'"
                  name="calculation_wizard.preview.sidebar_step_2"
                  default="Step 2"
                />
                <Translation
                  v-else
                  name="calculation_wizard.preview.sidebar_step_3"
                  default="Step 3"
                />
              </span>
              <ChevronDownWhite
                class="transition-transform"
                :class="{ 'rotate-180': stepsOpen[3] }"
              />
            </div>
            <div
              class="info-text accordion-content max-h-0"
              :class="stepsOpen[3] ? 'opacity-1 mt-4' : 'opacity-0'"
            >
              <p>
                <Translation
                  name="calculation_wizard.preview.sidebar_benefits_step_3_p1"
                  default="To be able to compare this to a living wage benchmark, the adjusted annual value is divided by 12 months."
                />
              </p>
              <div class="bg-[#2D2D32] rounded p-3 mt-3 border border-gray-600">
                <p class="text-xs text-gray-300 mb-2">
                  <Translation
                    name="calculation_wizard.preview.sidebar_calculation"
                    default="Calculation"
                  />:
                </p>
                <p class="text-sm text-gray-200">
                  <Translation
                    name="calculation_wizard.preview.sidebar_benefits_step_3_calculation_1"
                    default="Total annual in-kind benefits:"
                  />
                  <strong>{{
                    comparableAnnualInKindBenefits
                      ? comparableAnnualInKindBenefits.toFixed(2)
                      : "-"
                  }}</strong>
                  {{ currency }}
                </p>
                <p class="text-sm text-gray-200">
                  ÷ 12
                  <Translation
                    name="calculation_wizard.preview.sidebar_months"
                    default="months"
                  />
                </p>
                <hr class="my-2 border-gray-600" />
                <p class="text-sm text-gray-200">
                  =
                  <Translation
                    name="calculation_wizard.preview.sidebar_benefits_step_3_calculation_2"
                    default="Comparable monthly in-kind benefits:"
                  />
                  <strong>{{
                    comparableMonthlyInKindBenefits
                      ? comparableMonthlyInKindBenefits.toFixed(2)
                      : "-"
                  }}</strong>
                  {{ currency }}
                </p>
              </div>
              <p>
                <Translation
                  name="calculation_wizard.preview.sidebar_benefits_step_3_p2"
                  default="This number is displayed in your chart."
                />
              </p>
            </div>
          </div>
        </template>
        <template v-else>
          <div id="step-1" class="accordion">
            <div
              class="flex items-center justify-between cursor-pointer"
              @click="toggleStep(1)"
            >
              <span class="font-bold">
                <Translation
                  name="calculation_wizard.preview.sidebar_step_1"
                  default="Step 1"
                />
              </span>
              <ChevronDownWhite
                class="transition-transform"
                :class="{ 'rotate-180': stepsOpen[1] }"
              />
            </div>
            <div
              class="info-text accordion-content max-h-0"
              :class="stepsOpen[1] ? 'opacity-1 mt-4' : 'opacity-0'"
            >
              <p>
                <Translation
                  name="calculation_wizard.preview.sidebar_no_benefits_step_1_p1"
                  default="For this job category / individual, in-kind benefits remain within the maximum that is incorporated in a living wage benchmark. Therefore, the total annual value of your in-kind benefits is the sum of each:"
                />
              </p>
              <div class="bg-[#2D2D32] rounded p-3 mt-3 border border-gray-600">
                <p class="text-xs text-gray-300 mb-2">
                  <Translation
                    name="calculation_wizard.preview.sidebar_calculation"
                    default="Calculation"
                  />:
                </p>
                <p class="text-sm text-gray-200">
                  <Translation
                    name="calculation_wizard.preview.sidebar_benefits_housing"
                    default="Housing"
                  />:
                  <strong>{{
                    ikbHousing ? ikbHousing.toFixed(2) : "-"
                  }}</strong>
                  {{ currency }}
                </p>
                <p class="text-sm text-gray-200">
                  +
                  <Translation
                    name="calculation_wizard.preview.sidebar_benefits_childcare"
                    default="Childcare"
                  />:
                  <strong>{{
                    ikbChildcare ? ikbChildcare.toFixed(2) : "-"
                  }}</strong>
                  {{ currency }}
                </p>
                <p class="text-sm text-gray-200">
                  +
                  <Translation
                    name="calculation_wizard.preview.sidebar_benefits_child_education"
                    default="Child education"
                  />:
                  <strong>{{
                    ikbChildEducation ? ikbChildEducation.toFixed(2) : "-"
                  }}</strong>
                  {{ currency }}
                </p>
                <p class="text-sm text-gray-200">
                  +
                  <Translation
                    name="calculation_wizard.preview.sidebar_benefits_food"
                    default="Food"
                  />:
                  <strong>{{ ikbFood ? ikbFood.toFixed(2) : "-" }}</strong>
                  {{ currency }}
                </p>
                <p class="text-sm text-gray-200">
                  +
                  <Translation
                    name="calculation_wizard.preview.sidebar_benefits_healthcare"
                    default="Healthcare"
                  />:
                  <strong>{{
                    ikbHealthcare ? ikbHealthcare.toFixed(2) : "-"
                  }}</strong>
                  {{ currency }}
                </p>
                <p class="text-sm text-gray-200">
                  +
                  <Translation
                    name="calculation_wizard.preview.sidebar_benefits_transportation"
                    default="Transportation"
                  />:
                  <strong>{{
                    ikbTransportation ? ikbTransportation.toFixed(2) : "-"
                  }}</strong>
                  {{ currency }}
                </p>
                <hr class="my-2 border-gray-600" />
                <p class="text-sm text-gray-200">
                  =
                  <Translation
                    name="calculation_wizard.preview.sidebar_benefits_no_benefits_step_1_p2"
                    default="Total annual in-kind benefits:"
                  />
                  <strong>{{
                    comparableAnnualInKindBenefits
                      ? comparableAnnualInKindBenefits.toFixed(2)
                      : "-"
                  }}</strong>
                  {{ currency }}
                </p>
              </div>
            </div>
          </div>
          <div id="step-2" class="accordion">
            <div
              class="flex items-center justify-between cursor-pointer"
              @click="toggleStep(2)"
            >
              <span class="font-bold">
                <Translation
                  name="calculation_wizard.preview.sidebar_step_2"
                  default="Step 2"
                />
              </span>
              <ChevronDownWhite
                class="transition-transform"
                :class="{ 'rotate-180': stepsOpen[2] }"
              />
            </div>
            <div
              class="info-text accordion-content max-h-0"
              :class="stepsOpen[2] ? 'opacity-1 mt-4' : 'opacity-0'"
            >
              <p>
                <Translation
                  name="calculation_wizard.preview.sidebar_benefits_no_benefits_step_2_p1"
                  default="To be able to compare this to a living wage benchmark, the annual value is divided by 12 months."
                />
              </p>
              <div class="bg-[#2D2D32] rounded p-3 mt-3 border border-gray-600">
                <p class="text-xs text-gray-300 mb-2">
                  <Translation
                    name="calculation_wizard.preview.sidebar_calculation"
                    default="Calculation"
                  />:
                </p>
                <p class="text-sm text-gray-200">
                  <Translation
                    name="calculation_wizard.preview.sidebar_benefits_no_benefits_step_2_calculation_1"
                    default="Total annual in-kind benefits:"
                  />
                  <strong>{{
                    comparableAnnualInKindBenefits
                      ? comparableAnnualInKindBenefits.toFixed(2)
                      : "-"
                  }}</strong>
                  {{ currency }}
                </p>
                <p class="text-sm text-gray-200">
                  ÷ 12
                  <Translation
                    name="calculation_wizard.preview.sidebar_months"
                    default="months"
                  />
                </p>
                <hr class="my-2 border-gray-600" />
                <p class="text-sm text-gray-200">
                  =
                  <Translation
                    name="calculation_wizard.preview.sidebar_benefits_no_benefits_step_2_calculation_2"
                    default="Comparable monthly in-kind benefits:"
                  />
                  <strong>{{
                    comparableMonthlyInKindBenefits
                      ? comparableMonthlyInKindBenefits.toFixed(2)
                      : "-"
                  }}</strong>
                  {{ currency }}
                </p>
              </div>
              <p>
                <Translation
                  name="calculation_wizard.preview.sidebar_benefits_no_benefits_step_2_p2"
                  default="This number is displayed in your chart."
                />
              </p>
            </div>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>
<script setup>
import { ref, onMounted, computed } from "vue";
import ChevronDownWhite from "../../icons/ChevronDownWhite.vue";

const props = defineProps({
  currency: {
    type: String,
    required: true,
  },
  // User input values (7 in-kind benefits)
  ikbHousing: {
    type: Number,
    required: true,
  },
  ikbChildcare: {
    type: Number,
    required: true,
  },
  ikbChildEducation: {
    type: Number,
    required: true,
  },
  ikbFood: {
    type: Number,
    required: true,
  },
  ikbHealthcare: {
    type: Number,
    required: true,
  },
  ikbTransportation: {
    type: Number,
    required: true,
  },
  shareOfYearWorked: {
    type: Number,
    required: true,
  },
  comparableWageAndBonuses: {
    type: Number,
    required: true,
  },
  comparableAnnualInKindBenefits: {
    type: Number,
    required: true,
  },
  comparableMonthlyInKindBenefits: {
    type: Number,
    required: true,
  },
  capsApplied: {
    type: Boolean,
    required: true,
  },
  capsExceeded: {
    type: Boolean,
    required: true,
  },
  combinedCapsExceeded: {
    type: Boolean,
    required: true,
  },
  housingIkbCapPercentage: {
    type: Number,
    required: true,
  },
  otherIkbCapPercentage: {
    type: Number,
    required: true,
  },
  totalIkbCapPercentage: {
    type: Number,
    required: true,
  },
  jobCategory: {
    type: String,
    default: "",
  },
  gender: {
    type: String,
    default: "",
  },
});

const hasBenefits = computed(() => {
  return (
    (props.ikbHousing || 0) > 0 ||
    (props.ikbChildcare || 0) > 0 ||
    (props.ikbChildEducation || 0) > 0 ||
    (props.ikbFood || 0) > 0 ||
    (props.ikbHealthcare || 0) > 0 ||
    (props.ikbTransportation || 0) > 0
  );
});

const stepsOpen = ref({
  1: true,
  2: false,
  3: false,
});

// Computed properties for dynamic step numbering
const stepTwoLabel = computed(() => {
  if (props.capsExceeded && props.combinedCapsExceeded) {
    return "Step 2";
  }
  return "Step 1";
});

const stepThreeLabel = computed(() => {
  if (props.capsExceeded && props.combinedCapsExceeded) {
    return "Step 3";
  } else if (props.capsExceeded || props.combinedCapsExceeded) {
    return "Step 2";
  }
  return "Step 1";
});

// STEP 1: CALCULATE TOTAL ADJUSTED BENEFITS (AFTER INDIVIDUAL CAPS)
// This represents the sum of all benefits after applying the same individual caps
// that the backend uses. The cap percentages depend on how many IKBs are non-zero
// and are provided by the backend as props.
const totalAdjustedBenefits = computed(() => {
  const housingCapValue = Math.round(
    props.comparableWageAndBonuses * (props.housingIkbCapPercentage / 100)
  );
  const otherCapValue = Math.round(
    props.comparableWageAndBonuses * (props.otherIkbCapPercentage / 100)
  );

  const housing = Math.min(props.ikbHousing, housingCapValue);
  const childcare = Math.min(props.ikbChildcare, otherCapValue);
  const childEducation = Math.min(props.ikbChildEducation, otherCapValue);
  const food = Math.min(props.ikbFood, otherCapValue);
  const healthcare = Math.min(props.ikbHealthcare, otherCapValue);
  const transportation = Math.min(props.ikbTransportation, otherCapValue);

  return (
    housing + childcare + childEducation + food + healthcare + transportation
  );
});

// CALCULATE COMBINED CAP VALUE
// This is the maximum value of all in-kind benefits combined (42.9% of cash wages)
const combinedCapValue = computed(() => {
  return props.comparableWageAndBonuses * (props.totalIkbCapPercentage / 100);
});

const toggleStep = (step) => {
  const accordion = document.getElementById(`step-${step}`);
  if (!accordion) return;

  const accordionContent = accordion.querySelector(".accordion-content");
  if (!accordionContent) return;

  if (stepsOpen.value[step]) {
    accordionContent.style.maxHeight = 0;
  } else {
    // Force a reflow to get accurate scrollHeight
    accordionContent.style.maxHeight = "none";
    accordionContent.style.overflow = "visible";
    const height = accordionContent.scrollHeight;
    accordionContent.style.overflow = "hidden";
    accordionContent.style.maxHeight = `${height}px`;
  }
  stepsOpen.value[step] = !stepsOpen.value[step];
};

onMounted(() => {
  const accordion = document.getElementById("step-1");
  if (!accordion) return;

  const accordionContent = accordion.querySelector(".accordion-content");
  if (!accordionContent) return;

  // Force a reflow to get accurate scrollHeight
  accordionContent.style.maxHeight = "none";
  accordionContent.style.overflow = "visible";
  const height = accordionContent.scrollHeight;
  accordionContent.style.overflow = "hidden";
  accordionContent.style.maxHeight = `${height}px`;
});
</script>
